import { useAiReadingResultQuery } from '@entities/saju'
import { ErrorView, LoadingView } from '@features/onboarding'
import { FreeResultView, PaidResultView } from '@features/result'
import { capture, PosthogPageView } from '@shared/lib/posthog'
import { usePollingStore } from '@shared/store/use-polling-store'
import { NoSsrSuspense } from '@shared/ui/no-ssr-suspense'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

export const Route = createFileRoute('/saju/$sajuType/result')({
  component: ResultPage,
  validateSearch: (search: Record<string, unknown>): { hashKey: string } => {
    const hashKey = search.hashKey as string
    if (!hashKey) {
      throw new Error('분석 결과를 찾을 수 없어요. 사주 풀이를 먼저 진행해 주세요.')
    }
    return { hashKey }
  },
})

function ResultPage() {
  const { sajuType } = Route.useParams()
  const { hashKey } = Route.useSearch()
  const navigate = useNavigate()

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          fallbackRender={({ error, resetErrorBoundary }) => (
            <ErrorView
              error={error instanceof Error ? error : new Error(String(error))}
              onRetry={resetErrorBoundary}
              onHome={() => {
                void navigate({
                  to: '/saju/$sajuType',
                  params: { sajuType },
                })
              }}
            />
          )}
        >
          <NoSsrSuspense fallback={<LoadingView />}>
            <ResultContent sajuType={sajuType} hashKey={hashKey} />
          </NoSsrSuspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  )
}

function ResultContent({
  sajuType,
  hashKey,
}: {
  readonly sajuType: string
  readonly hashKey: string
}) {
  const navigate = useNavigate()
  const setViewed = usePollingStore((s) => s.setViewed)
  const remove = usePollingStore((s) => s.remove)

  const { data } = useAiReadingResultQuery(sajuType, hashKey)

  // 결과 확인 시 store 동기화 (토스트/배너용)
  useEffect(() => {
    if (data) {
      setViewed(hashKey)
    }
  }, [hashKey, data, setViewed])

  const handleReset = () => {
    remove(hashKey)
    void navigate({
      to: '/saju/$sajuType',
      params: { sajuType },
    })
  }

  // 폴링 중 (COMPLETED 전) → select가 null 반환 → 로딩 유지
  if (!data) {
    return <LoadingView />
  }

  if (data.paid) {
    return (
      <PaidResultView
        result={data.result}
        sajuType={sajuType}
        hashKey={hashKey}
        onReset={handleReset}
        onStepChange={(step) => {
          capture('view_result_paid_step', { step })
        }}
      />
    )
  }

  return (
    <PosthogPageView event="view_result_free">
      <FreeResultView result={data.result} hashKey={hashKey} onReset={handleReset} />
    </PosthogPageView>
  )
}
